/**
 * Validation Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides comprehensive validation functions for forms,
 * data structures, and user inputs used throughout the application.
 */

import { VALIDATION_RULES, USER_ROLES, SUBSCRIPTION_STATUS } from '../constants';

/**
 * Basic Input Validation
 */
export const basicValidators = {
  /**
   * Validate email address
   * @param {string} email - Email to validate
   * @returns {Object} Validation result
   */
  validateEmail: function(email) {
    if (!email || typeof email !== 'string') {
      return {
        isValid: false,
        error: 'Email is required',
        errorBg: 'Имейлът е задължителен'
      };
    }

    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      return {
        isValid: false,
        error: 'Email is required',
        errorBg: 'Имейлът е задължителен'
      };
    }

    if (trimmedEmail.length > 255) {
      return {
        isValid: false,
        error: 'Email is too long',
        errorBg: 'Имейлът е твърде дълъг'
      };
    }

    if (!VALIDATION_RULES.EMAIL_REGEX.test(trimmedEmail)) {
      return {
        isValid: false,
        error: 'Invalid email format',
        errorBg: 'Невалиден формат на имейл'
      };
    }

    return {
      isValid: true,
      value: trimmedEmail.toLowerCase()
    };
  },

  /**
   * Validate password
   * @param {string} password - Password to validate
   * @returns {Object} Validation result
   */
  validatePassword: function(password) {
    if (!password || typeof password !== 'string') {
      return {
        isValid: false,
        error: 'Password is required',
        errorBg: 'Паролата е задължителна'
      };
    }

    if (password.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
      return {
        isValid: false,
        error: 'Password must be at least ' + VALIDATION_RULES.PASSWORD_MIN_LENGTH + ' characters',
        errorBg: 'Паролата трябва да е поне ' + VALIDATION_RULES.PASSWORD_MIN_LENGTH + ' символа'
      };
    }

    // Check for basic password requirements (optional strong validation)
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasLower || !hasUpper || !hasNumber) {
      return {
        isValid: false,
        error: 'Password must contain uppercase, lowercase, and number',
        errorBg: 'Паролата трябва да съдържа главна буква, малка буква и цифра'
      };
    }

    return {
      isValid: true,
      value: password
    };
  },

  /**
   * Validate password confirmation
   * @param {string} password - Original password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Object} Validation result
   */
  validatePasswordConfirmation: function(password, confirmPassword) {
    if (!confirmPassword || typeof confirmPassword !== 'string') {
      return {
        isValid: false,
        error: 'Password confirmation is required',
        errorBg: 'Потвърждението на паролата е задължително'
      };
    }

    if (password !== confirmPassword) {
      return {
        isValid: false,
        error: 'Passwords do not match',
        errorBg: 'Паролите не съвпадат'
      };
    }

    return {
      isValid: true,
      value: confirmPassword
    };
  },

  /**
   * Validate required field
   * @param {any} value - Value to validate
   * @param {string} fieldName - Field name for error message
   * @returns {Object} Validation result
   */
  validateRequired: function(value, fieldName) {
    if (value === null || value === undefined || value === '') {
      return {
        isValid: false,
        error: fieldName + ' is required',
        errorBg: fieldName + ' е задължително'
      };
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      return {
        isValid: false,
        error: fieldName + ' is required',
        errorBg: fieldName + ' е задължително'
      };
    }

    return {
      isValid: true,
      value: value
    };
  }
};

/**
 * Surebet-Specific Validation
 */
export const surebetValidators = {
  /**
   * Validate stake amount
   * @param {number|string} stake - Stake amount to validate
   * @returns {Object} Validation result
   */
  validateStake: function(stake) {
    if (stake === null || stake === undefined || stake === '') {
      return {
        isValid: false,
        error: 'Stake amount is required',
        errorBg: 'Сумата за залагане е задължителна'
      };
    }

    const stakeNumber = typeof stake === 'string' ? parseFloat(stake.replace(',', '.')) : stake;

    if (isNaN(stakeNumber)) {
      return {
        isValid: false,
        error: 'Invalid stake amount',
        errorBg: 'Невалидна сума за залагане'
      };
    }

    if (stakeNumber < VALIDATION_RULES.STAKE_MIN) {
      return {
        isValid: false,
        error: 'Minimum stake is ' + VALIDATION_RULES.STAKE_MIN + ' EUR',
        errorBg: 'Минималният залог е ' + VALIDATION_RULES.STAKE_MIN + ' €'
      };
    }

    if (stakeNumber > VALIDATION_RULES.STAKE_MAX) {
      return {
        isValid: false,
        error: 'Maximum stake is ' + VALIDATION_RULES.STAKE_MAX + ' EUR',
        errorBg: 'Максималният залог е ' + VALIDATION_RULES.STAKE_MAX + ' €'
      };
    }

    return {
      isValid: true,
      value: stakeNumber
    };
  },

  /**
   * Validate margin value
   * @param {number} margin - Margin value to validate
   * @returns {Object} Validation result
   */
  validateMargin: function(margin) {
    if (margin === null || margin === undefined) {
      return {
        isValid: false,
        error: 'Margin value is required',
        errorBg: 'Стойността на маржа е задължителна'
      };
    }

    if (isNaN(margin)) {
      return {
        isValid: false,
        error: 'Invalid margin value',
        errorBg: 'Невалидна стойност на марж'
      };
    }

    if (margin < VALIDATION_RULES.MARGIN_MIN) {
      return {
        isValid: false,
        error: 'Margin is too low for profitable arbitrage',
        errorBg: 'Маржът е твърде нисък за печеливш арбитраж'
      };
    }

    return {
      isValid: true,
      value: margin
    };
  },

  /**
   * Validate MarketsAnalysisDTO structure
   * @param {Object} data - MarketsAnalysisDTO object
   * @returns {Object} Validation result
   */
  validateMarketsAnalysisDTO: function(data) {
    if (!data || typeof data !== 'object') {
      return {
        isValid: false,
        error: 'Invalid surebet data structure'
      };
    }

    // Check required fields
    if (!data.id || typeof data.id !== 'number') {
      return {
        isValid: false,
        error: 'Missing or invalid surebet ID'
      };
    }

    if (!data.event || typeof data.event !== 'object') {
      return {
        isValid: false,
        error: 'Missing event data'
      };
    }

    // Validate event structure
    const requiredEventFields = ['id', 'date', 'time', 'teamOne', 'teamTwo', 'championship', 'sport'];
    for (let i = 0; i < requiredEventFields.length; i++) {
      const field = requiredEventFields[i];
      if (!data.event[field]) {
        return {
          isValid: false,
          error: 'Missing event field: ' + field
        };
      }
    }

    // Check marginFlag
    if (typeof data.marginFlag !== 'boolean') {
      return {
        isValid: false,
        error: 'Invalid marginFlag value'
      };
    }

    // Validate margins object (can be empty)
    if (data.margins && typeof data.margins !== 'object') {
      return {
        isValid: false,
        error: 'Invalid margins data structure'
      };
    }

    return {
      isValid: true,
      value: data
    };
  }
};

/**
 * Form Validation Utilities
 */
export const formValidators = {
  /**
   * Validate entire form object
   * @param {Object} formData - Form data to validate
   * @param {Object} validationRules - Validation rules object
   * @returns {Object} Validation result with field-specific errors
   */
  validateForm: function(formData, validationRules) {
    const errors = {};
    let isValid = true;

    if (!formData || typeof formData !== 'object') {
      return {
        isValid: false,
        errors: { form: 'Invalid form data' },
        hasErrors: true
      };
    }

    if (!validationRules || typeof validationRules !== 'object') {
      return {
        isValid: true,
        errors: {},
        hasErrors: false
      };
    }

    // Validate each field
    Object.keys(validationRules).forEach(function(fieldName) {
      const rules = validationRules[fieldName];
      const value = formData[fieldName];
      const fieldErrors = [];

      // Required validation
      if (rules.required) {
        const requiredResult = basicValidators.validateRequired(value, fieldName);
        if (!requiredResult.isValid) {
          fieldErrors.push(requiredResult.errorBg || requiredResult.error);
        }
      }

      // Skip other validations if value is empty and not required
      if (!rules.required && (value === null || value === undefined || value === '')) {
        return;
      }

      // Email validation
      if (rules.email) {
        const emailResult = basicValidators.validateEmail(value);
        if (!emailResult.isValid) {
          fieldErrors.push(emailResult.errorBg || emailResult.error);
        }
      }

      // Password validation
      if (rules.password) {
        const passwordResult = basicValidators.validatePassword(value);
        if (!passwordResult.isValid) {
          fieldErrors.push(passwordResult.errorBg || passwordResult.error);
        }
      }

      // Stake validation
      if (rules.stake) {
        const stakeResult = surebetValidators.validateStake(value);
        if (!stakeResult.isValid) {
          fieldErrors.push(stakeResult.errorBg || stakeResult.error);
        }
      }

      // Custom validation function
      if (rules.custom && typeof rules.custom === 'function') {
        const customResult = rules.custom(value);
        if (customResult && !customResult.isValid) {
          fieldErrors.push(customResult.errorBg || customResult.error);
        }
      }

      // Add field errors if any
      if (fieldErrors.length > 0) {
        errors[fieldName] = fieldErrors;
        isValid = false;
      }
    });

    return {
      isValid: isValid,
      errors: errors,
      hasErrors: !isValid
    };
  },

  /**
   * Get validation rules for login form
   * @returns {Object} Login form validation rules
   */
  getLoginValidationRules: function() {
    return {
      email: {
        required: true,
        email: true
      },
      password: {
        required: true
      }
    };
  },

  /**
   * Get validation rules for registration form
   * @returns {Object} Registration form validation rules
   */
  getRegistrationValidationRules: function() {
    return {
      email: {
        required: true,
        email: true
      },
      password: {
        required: true,
        password: true
      },
      confirmPassword: {
        required: true,
        custom: function(value, formData) {
          return basicValidators.validatePasswordConfirmation(formData.password, value);
        }
      }
    };
  },

  /**
   * Get validation rules for calculator form
   * @returns {Object} Calculator form validation rules
   */
  getCalculatorValidationRules: function() {
    return {
      totalStake: {
        required: true,
        stake: true
      }
    };
  }
};

/**
 * Data Structure Validators
 */
export const dataValidators = {
  /**
   * Validate user object structure
   * @param {Object} user - User object to validate
   * @returns {Object} Validation result
   */
  validateUser: function(user) {
    if (!user || typeof user !== 'object') {
      return {
        isValid: false,
        error: 'Invalid user data'
      };
    }

    // Check required fields
    const requiredFields = ['id', 'email', 'role'];
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      if (!user[field]) {
        return {
          isValid: false,
          error: 'Missing user field: ' + field
        };
      }
    }

    // Validate email
    const emailValidation = basicValidators.validateEmail(user.email);
    if (!emailValidation.isValid) {
      return emailValidation;
    }

    // Validate role
    const validRoles = Object.values(USER_ROLES);
    if (!validRoles.includes(user.role)) {
      return {
        isValid: false,
        error: 'Invalid user role: ' + user.role
      };
    }

    return {
      isValid: true,
      value: user
    };
  },

  /**
   * Validate subscription object structure
   * @param {Object} subscription - Subscription object to validate
   * @returns {Object} Validation result
   */
  validateSubscription: function(subscription) {
    if (!subscription || typeof subscription !== 'object') {
      return {
        isValid: false,
        error: 'Invalid subscription data'
      };
    }

    // Check required fields
    if (!subscription.status) {
      return {
        isValid: false,
        error: 'Missing subscription status'
      };
    }

    // Validate status
    const validStatuses = Object.values(SUBSCRIPTION_STATUS);
    if (!validStatuses.includes(subscription.status)) {
      return {
        isValid: false,
        error: 'Invalid subscription status: ' + subscription.status
      };
    }

    // Validate dates if present
    if (subscription.expiryDate) {
      const expiryDate = new Date(subscription.expiryDate);
      if (isNaN(expiryDate.getTime())) {
        return {
          isValid: false,
          error: 'Invalid subscription expiry date'
        };
      }
    }

    return {
      isValid: true,
      value: subscription
    };
  },

  /**
   * Validate coefficient object structure
   * @param {Object} coefficient - Coefficient object to validate
   * @returns {Object} Validation result
   */
  validateCoefficient: function(coefficient) {
    if (!coefficient || typeof coefficient !== 'object') {
      return {
        isValid: false,
        error: 'Invalid coefficient data'
      };
    }

    // Check required fields
    const requiredFields = ['bookmaker', 'type', 'value'];
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      if (!coefficient[field]) {
        return {
          isValid: false,
          error: 'Missing coefficient field: ' + field
        };
      }
    }

    // Validate odds value
    if (typeof coefficient.value !== 'number' || coefficient.value <= 1) {
      return {
        isValid: false,
        error: 'Invalid odds value: must be number > 1'
      };
    }

    return {
      isValid: true,
      value: coefficient
    };
  }
};

/**
 * Business Logic Validators
 */
export const businessValidators = {
  /**
   * Validate arbitrage calculation inputs
   * @param {Array} coefficients - Array of coefficient objects
   * @param {number} totalStake - Total stake amount
   * @returns {Object} Validation result
   */
  validateArbitrageInputs: function(coefficients, totalStake) {
    // Validate coefficients array
    if (!Array.isArray(coefficients) || coefficients.length < 2) {
      return {
        isValid: false,
        error: 'At least 2 coefficients required for arbitrage',
        errorBg: 'Необходими са поне 2 коефициента за арбитраж'
      };
    }

    // Validate each coefficient
    for (let i = 0; i < coefficients.length; i++) {
      const coefValidation = dataValidators.validateCoefficient(coefficients[i]);
      if (!coefValidation.isValid) {
        return {
          isValid: false,
          error: 'Invalid coefficient at index ' + i + ': ' + coefValidation.error,
          errorBg: 'Невалиден коефициент на позиция ' + i + ': ' + coefValidation.error
        };
      }
    }

    // Validate total stake
    const stakeValidation = surebetValidators.validateStake(totalStake);
    if (!stakeValidation.isValid) {
      return stakeValidation;
    }

    // Check if arbitrage is mathematically possible
    const inverseSum = coefficients.reduce(function(sum, coef) {
      return sum + (1 / coef.value);
    }, 0);

    if (inverseSum >= 1) {
      return {
        isValid: false,
        error: 'No arbitrage opportunity: inverse sum >= 1',
        errorBg: 'Няма арбитражна възможност: сумата на обратните стойности >= 1'
      };
    }

    return {
      isValid: true,
      value: {
        coefficients: coefficients,
        totalStake: stakeValidation.value,
        margin: 1 - inverseSum
      }
    };
  },

  /**
   * Validate bookmaker filter selection
   * @param {Array} selectedBookmakers - Array of selected bookmaker IDs
   * @returns {Object} Validation result
   */
  validateBookmakerFilter: function(selectedBookmakers) {
    if (!Array.isArray(selectedBookmakers)) {
      return {
        isValid: false,
        error: 'Invalid bookmaker selection',
        errorBg: 'Невалидна селекция на букмейкъри'
      };
    }

    if (selectedBookmakers.length === 0) {
      return {
        isValid: false,
        error: 'At least one bookmaker must be selected',
        errorBg: 'Трябва да изберете поне един букмейкър'
      };
    }

    // Validate each bookmaker ID
    const validBookmakers = Object.values({
      'betano': true, 'bet365': true, 'winbet': true, 'betwild': true, 'slybet': true, 'bwin': true,
      'efbet': true, 'inbet': true, 'alphawin': true,
      'sesame': true, 'elitbet': true, 'bethub': true, 'bet': true,
      'topwin': true, 'mrbit': true, 'admiralbet': true, 'bg8888': true,
      'palmsbet': true, 'everbet': true, 'magicbet': true,
      'betvam': true, 'qbet': true, 'imperiabet': true
    });

    for (let i = 0; i < selectedBookmakers.length; i++) {
      const bookmaker = selectedBookmakers[i];
      if (!validBookmakers[bookmaker]) {
        return {
          isValid: false,
          error: 'Invalid bookmaker: ' + bookmaker,
          errorBg: 'Невалиден букмейкър: ' + bookmaker
        };
      }
    }

    return {
      isValid: true,
      value: selectedBookmakers
    };
  }
};

/**
 * Security Validators
 */
export const securityValidators = {
  /**
   * Validate JWT token format
   * @param {string} token - JWT token to validate
   * @returns {Object} Validation result
   */
  validateJWTToken: function(token) {
    if (!token || typeof token !== 'string') {
      return {
        isValid: false,
        error: 'Invalid token format'
      };
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return {
        isValid: false,
        error: 'Invalid JWT token structure'
      };
    }

    // Check if token is expired (basic check)
    try {
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < currentTime) {
        return {
          isValid: false,
          error: 'Token has expired',
          isExpired: true
        };
      }

      return {
        isValid: true,
        value: token,
        payload: payload
      };
    } catch (error) {
      return {
        isValid: false,
        error: 'Invalid token payload'
      };
    }
  },

  /**
   * Validate user input for XSS prevention
   * @param {string} input - User input to validate
   * @returns {Object} Validation result
   */
  validateUserInput: function(input) {
    if (!input || typeof input !== 'string') {
      return {
        isValid: true,
        value: ''
      };
    }

    // Check for potential XSS patterns
    const xssPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<embed/gi
    ];

    for (let i = 0; i < xssPatterns.length; i++) {
      if (xssPatterns[i].test(input)) {
        return {
          isValid: false,
          error: 'Input contains potentially dangerous content',
          errorBg: 'Въведеният текст съдържа потенциално опасно съдържание'
        };
      }
    }

    return {
      isValid: true,
      value: input.trim()
    };
  }
};

/**
 * Validation Helper Functions
 */
export const validationHelpers = {
  /**
   * Combine multiple validation results
   * @param {Array} validationResults - Array of validation result objects
   * @returns {Object} Combined validation result
   */
  combineValidations: function(validationResults) {
    if (!Array.isArray(validationResults)) {
      return {
        isValid: false,
        errors: ['Invalid validation results array']
      };
    }

    let allValid = true;
    const allErrors = [];

    validationResults.forEach(function(result) {
      if (result && !result.isValid) {
        allValid = false;
        if (result.error) {
          allErrors.push(result.error);
        }
        if (result.errorBg) {
          allErrors.push(result.errorBg);
        }
      }
    });

    return {
      isValid: allValid,
      errors: allErrors,
      hasErrors: allErrors.length > 0
    };
  },

  /**
   * Create validation error object
   * @param {string} field - Field name
   * @param {string} message - Error message
   * @param {string} messageBg - Bulgarian error message
   * @returns {Object} Validation error object
   */
  createValidationError: function(field, message, messageBg) {
    return {
      field: field,
      message: message,
      messageBg: messageBg,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Export all validators
 */
export default {
  basic: basicValidators,
  surebet: surebetValidators,
  form: formValidators,
  data: dataValidators,
  security: securityValidators,
  helpers: validationHelpers
};
